import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import {
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';

import { LabeledField } from '@/__core/components/LabeledField';

interface ToolbarListReportProps {
  searchQuery: string;
  status: string;
  companyId: string;
  companyDisabled: boolean;
  companyOptions: Array<{ company_id: string; company_name: string }>;
  statusOptions: Array<{ id: string; name: string }>;
  onSearchChange: (query: string) => void;
  onStatusChange: (status: string) => void;
  onCompanyChange: (companyId: string) => void;
  onRefresh?: () => void;
}

export default function ToolbarListReport({
  searchQuery,
  status,
  companyId,
  companyDisabled,
  companyOptions,
  statusOptions,
  onSearchChange,
  onStatusChange,
  onCompanyChange,
  onRefresh,
}: ToolbarListReportProps) {
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (status && status !== 'all') count += 1;
    if (companyId) count += 1;
    return count;
  }, [companyId, status]);

  return (
    <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid #e5e7eb', backgroundColor: '#fafafa' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <TextField
            size='small'
            placeholder='Tìm theo mã báo cáo, tên báo cáo, tham chiếu, ghi chú...'
            value={searchQuery}
            onChange={(event) => onSearchChange(event.target.value)}
            sx={{
              width: 400,
              '& .MuiOutlinedInput-root': { borderRadius: 1, height: '30px', fontSize: '0.875rem' },
              '& .MuiInputBase-input': {
                padding: '6px 12px',
                '&::placeholder': { fontSize: '0.813rem', opacity: 0.7 },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon sx={{ fontSize: '1rem', color: '#6b7280' }} />
                </InputAdornment>
              ),
            }}
          />

          <Button
            variant='outlined'
            startIcon={<FilterListIcon />}
            onClick={() => setIsFilterOpen((current) => !current)}
            sx={{
              borderRadius: 1,
              textTransform: 'none',
              fontWeight: 500,
              minHeight: '22px !important',
              minWidth: 'auto !important',
              padding: '4px 12px !important',
              height: '28px !important',
              borderColor: '#d1d5db',
              marginLeft: '10px',
              color: '#374151',
              backgroundColor: isFilterOpen ? '#f3f4f6' : 'transparent',
              '&:hover': { borderColor: '#9ca3af', backgroundColor: '#f9fafb' },
            }}
          >
            Tìm kiếm nâng cao
            {isFilterOpen ? ' ↑' : ' ↓'}
          </Button>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant='contained'
            startIcon={<RefreshIcon />}
            onClick={onRefresh}
            sx={{
              backgroundColor: '#10b981',
              borderRadius: 0.5,
              textTransform: 'none',
              fontWeight: 600,
              minHeight: '22px !important',
              minWidth: 'auto !important',
              padding: '4px 12px !important',
              height: '30px !important',
              boxShadow: '0 1px 3px 0 rgba(16, 185, 129, 0.3)',
              '&:hover': { backgroundColor: '#059669', boxShadow: '0 4px 6px -1px rgba(16, 185, 129, 0.3)' },
            }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      <Collapse in={isFilterOpen} timeout='auto' unmountOnExit>
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Grid container rowSpacing={0} columnSpacing={4}>
            <Grid size={{ xs: 12, md: 6 }}>
              <LabeledField label='Trạng thái' labelWidth={90}>
                <TextField select size='small' value={status} onChange={(event) => onStatusChange(event.target.value)} fullWidth>
                  {statusOptions.map((option) => (
                    <MenuItem key={option.id} value={option.id}>{option.name}</MenuItem>
                  ))}
                </TextField>
              </LabeledField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <LabeledField label='Công ty' labelWidth={90}>
                <TextField select size='small' value={companyId} onChange={(event) => onCompanyChange(event.target.value)} disabled={companyDisabled} fullWidth>
                  {companyOptions.map((company) => (
                    <MenuItem key={company.company_id} value={company.company_id}>{company.company_name}</MenuItem>
                  ))}
                </TextField>
              </LabeledField>
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: '0.813rem', color: '#6b7280' }}>
              {activeFilterCount > 0 ? `${activeFilterCount} filter đang được áp dụng.` : 'Chưa có filter nâng cao nào được áp dụng.'}
            </Typography>
            <Button
              variant='text'
              onClick={() => {
                onStatusChange('all');
                if (!companyDisabled && companyOptions[0]) {
                  onCompanyChange(companyOptions[0].company_id);
                }
              }}
              sx={{ textTransform: 'none' }}
            >
              Reset filter
            </Button>
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
}